import React from 'react';
import {
  AlertTriangle,
  ClipboardList,
  Compass,
  HeartHandshake,
  LineChart,
  Radar,
  Shield,
  Trees,
  Users,
  Waypoints,
} from 'lucide-react';

const Work = () => {
  const revealRefs = React.useRef([]);
  const [visible, setVisible] = React.useState({});

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.getAttribute('data-reveal-index');
            setVisible((prev) => ({ ...prev, [index]: true }));
          }
        });
      },
      { threshold: 0.18 }
    );

    revealRefs.current.forEach((node) => node && observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const revealClass = (index) =>
    `transition-all duration-700 ease-out ${
      visible[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
    }`;

  return (
    <div className="min-h-screen bg-[#0d1b2a] text-slate-100">
      {/* Hero */}
      <main className="relative overflow-hidden border-b border-cyan-400/15 pt-32 pb-20 mesh-bg">
        <div className="absolute inset-0 opacity-50">
          <div className="absolute left-[10%] top-24 h-20 w-20 rounded-full bg-cyan-300/20 blur-xl animate-float" />
          <div className="absolute right-[15%] top-40 h-28 w-28 rounded-full bg-sky-400/15 blur-2xl animate-drift" />
          <div className="absolute bottom-16 left-1/3 h-24 w-24 rounded-full bg-emerald-300/15 blur-xl animate-float" />
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10">
          <p className="mb-4 inline-flex items-center rounded-full border border-cyan-300/35 bg-cyan-300/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200">
            Our Work
          </p>
          <h1 className="max-w-4xl text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
            Strategic Areas of Focus
          </h1>
          <div className="mt-5 h-1 w-36 rounded-full bg-gradient-to-r from-cyan-300 via-sky-300 to-transparent" />
          <p className="mt-7 max-w-3xl text-lg leading-relaxed text-slate-200/95 md:text-xl">
            We strengthen community resilience before, during, and after disasters through coordinated action, capacity
            building, and evidence-based advocacy.
          </p>
        </div>
      </main>

      {/* Prevention and Mitigation */}
      <section className="border-b border-cyan-400/10 py-16">
        <div
          ref={(el) => (revealRefs.current[0] = el)}
          data-reveal-index="0"
          className={`mx-auto w-full max-w-7xl px-6 lg:px-10 ${revealClass(0)}`}
        >
          <article className="rounded-2xl border border-cyan-300/30 bg-slate-950/40 p-6 shadow-cyan/40 lg:p-8">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-200/40 bg-cyan-300/10 text-cyan-200">
              <Shield size={20} />
            </div>
            <h2 className="text-2xl font-semibold text-white">Prevention and Mitigation</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-200/90">
              We support members and partners to reduce hazard exposure and vulnerability before disasters strike,
              combining structural and non-structural measures with community-led risk awareness and safer development
              practices.
            </p>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <li className="flex gap-3 rounded-xl border border-cyan-300/20 bg-slate-900/50 p-4 text-sm leading-6 text-slate-100">
                <span className="mt-0.5 shrink-0 text-cyan-300">
                  <LineChart size={16} />
                </span>
                <span>
                  <strong>Risk-informed planning:</strong> Integrating hazard and vulnerability analysis into programmes,
                  infrastructure choices, and local development plans.
                </span>
              </li>
              <li className="flex gap-3 rounded-xl border border-cyan-300/20 bg-slate-900/50 p-4 text-sm leading-6 text-slate-100">
                <span className="mt-0.5 shrink-0 text-cyan-300">
                  <Trees size={16} />
                </span>
                <span>
                  <strong>Environmental mitigation:</strong> Promoting nature-based solutions and sustainable land and
                  water management to lessen disaster drivers such as floods and landslides.
                </span>
              </li>
              <li className="flex gap-3 rounded-xl border border-cyan-300/20 bg-slate-900/50 p-4 text-sm leading-6 text-slate-100 sm:col-span-2 lg:col-span-1">
                <span className="mt-0.5 shrink-0 text-cyan-300">
                  <Users size={16} />
                </span>
                <span>
                  <strong>Community prevention:</strong> Supporting public awareness, safe behaviour campaigns, and
                  inclusive engagement so at-risk groups can act early and influence local risk decisions.
                </span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* Disaster Preparedness & Emergency Response */}
      <section className="py-16">
        <div
          ref={(el) => (revealRefs.current[1] = el)}
          data-reveal-index="1"
          className={`mx-auto grid w-full max-w-7xl gap-8 px-6 lg:grid-cols-2 lg:px-10 ${revealClass(1)}`}
        >
          <article className="rounded-2xl border border-cyan-300/30 bg-slate-950/40 p-6 shadow-cyan/40">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-200/40 bg-cyan-300/10 text-cyan-200">
              <Radar size={20} />
            </div>
            <h2 className="text-2xl font-semibold text-white">Disaster Preparedness</h2>
            <p className="mt-4 text-base leading-7 text-slate-200/90">
              Our disaster preparedness work ensures communities and responders are ready to act before crises escalate,
              reducing impact and saving lives.
            </p>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-100">
              <li className="flex gap-3">
                <span className="mt-1 text-cyan-300">
                  <Compass size={16} />
                </span>
                <span>
                  <strong>Planning and early warning:</strong> Supporting contingency planning, early warning systems,
                  and scenario development so actors can respond quickly and effectively.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1 text-cyan-300">
                  <LineChart size={16} />
                </span>
                <span>
                  <strong>Risk reduction activities:</strong> Promoting community-led initiatives that identify, reduce,
                  and manage disaster risks in vulnerable areas.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1 text-cyan-300">
                  <Waypoints size={16} />
                </span>
                <span>
                  <strong>Preparedness coordination:</strong> Bringing together NGOs, government, and partners to
                  harmonize preparedness plans and standard operating procedures.
                </span>
              </li>
            </ul>
          </article>

          <article className="rounded-2xl border border-cyan-300/30 bg-slate-950/40 p-6 shadow-cyan/40">
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-200/40 bg-cyan-300/10 text-cyan-200">
              <AlertTriangle size={20} />
            </div>
            <h2 className="text-2xl font-semibold text-white">Emergency Response</h2>
            <p className="mt-4 text-base leading-7 text-slate-200/90">
              During emergencies we coordinate and support principled, timely, and effective humanitarian responses that
              place affected people at the center.
            </p>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-100">
              <li className="flex gap-3">
                <span className="mt-1 text-cyan-300">
                  <ClipboardList size={16} />
                </span>
                <span>
                  <strong>Coordinating humanitarian response:</strong> Facilitating joint planning and information
                  sharing among responders to avoid gaps and duplication.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1 text-cyan-300">
                  <HeartHandshake size={16} />
                </span>
                <span>
                  <strong>Supporting affected communities:</strong> Ensuring that assistance is guided by community priorities, 
                  feedback and protection needs including people with disabilities, women, children and girls.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1 text-cyan-300">
                  <Users size={16} />
                </span>
                <span>
                  <strong>Rapid response coordination:</strong> Activating rapid coordination mechanisms so that
                  resources and teams reach the most affected areas quickly.
                </span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      {/* Joint Assessments */}
      <section className="border-y border-cyan-400/10 bg-slate-950/25 py-16">
        <div
          ref={(el) => (revealRefs.current[2] = el)}
          data-reveal-index="2"
          className={`mx-auto w-full max-w-7xl px-6 lg:px-10 ${revealClass(2)}`}
        >
          <div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div>
              <h2 className="text-2xl font-semibold text-white">Joint Assessments</h2>
              <p className="mt-4 text-base leading-7 text-slate-200/90">
                We promote joint assessments as a foundation for coordinated and evidence-based decision making across
                the humanitarian community.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <article className="rounded-xl border border-cyan-300/25 bg-slate-900/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-cyan-200">
                  Damage &amp; Needs
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-100">
                  Conducting and supporting multi-agency assessments to understand the scale and nature of impact.
                </p>
              </article>
              <article className="rounded-xl border border-cyan-300/25 bg-slate-900/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-cyan-200">Risk Analysis</p>
                <p className="mt-2 text-sm leading-6 text-slate-100">
                  Analyzing hazards, exposure, and vulnerabilities to guide preparedness and response planning.
                </p>
              </article>
              <article className="rounded-xl border border-cyan-300/25 bg-slate-900/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-cyan-200">Data Sharing</p>
                <p className="mt-2 text-sm leading-6 text-slate-100">
                  Encouraging common tools, standards, and platforms for sharing assessment data and analysis.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Capacity Building & Research */}
      <section className="py-16">
        <div
          ref={(el) => (revealRefs.current[3] = el)}
          data-reveal-index="3"
          className={`mx-auto w-full max-w-7xl px-6 lg:px-10 ${revealClass(3)}`}
        >
          <div className="grid gap-8 lg:grid-cols-2">
            <article className="rounded-2xl border border-cyan-300/30 bg-slate-950/40 p-6">
              <h2 className="text-2xl font-semibold text-white">Capacity Building</h2>
              <p className="mt-4 text-base leading-7 text-slate-200/90">
                Strengthening the skills and systems of NGOs, networks, and local institutions is central to our
                mandate.
              </p>
              <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-100">
                <li>
                  <strong>Training:</strong> Delivering tailored trainings on humanitarian standards, coordination, and
                  technical sectors.
                </li>
                <li>
                  <strong>Simulation exercises:</strong> Organizing drills and simulations that test and refine
                  emergency preparedness plans.
                </li>
                <li>
                  <strong>Workshops:</strong> Convening practitioners to share experiences, tools, and lessons learned.
                </li>
              </ul>
            </article>

            <article className="rounded-2xl border border-cyan-300/30 bg-slate-950/40 p-6">
              <h2 className="text-2xl font-semibold text-white">Research &amp; Learning</h2>
              <p className="mt-4 text-base leading-7 text-slate-200/90">
                We invest in learning, innovation, and advocacy to continuously improve disaster management and risk
                reduction practice.
              </p>
              <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-100">
                <li>
                  <strong>Disaster management research:</strong> Generating evidence on what works in prevention, mitigation, preparedness,
                  response, and recovery.
                </li>
                <li>
                  <strong>Best practice documentation:</strong> Capturing and sharing successful approaches, tools, and
                  case studies from our members.
                </li>
                <li>
                  <strong>Knowledge sharing:</strong> Creating platforms and spaces for continuous exchange among NGOs
                  and partners.
                </li>
                <li>
                  <strong>Advocacy &amp; policy engagement:</strong> Engaging decision-makers and contributing NGO
                  perspectives to disaster management frameworks.
                </li>
                <li>
                  <strong>Disaster risk reduction awareness:</strong> Raising public awareness on risks and practical
                  measures that communities can take to stay safe.
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Work;

